import {
  useDirection,
  useProperty,
  useSiteContext,
  useSnowplowTracking,
  useTranslation,
} from '@eti/providers';
import { Inline, Stack, Text } from '@etg/wings';
import { Link } from 'react-router-dom';
import { Share } from '@phosphor-icons/react';
import { useAuth } from '@eti/authentication';
import type { MouseEvent } from 'react';
import TopicsContentWrapper from '../TopicsContentWrapper';
import { linkStyles } from '../../../../../utils/styleUtils';
import { routes } from '../../../../../constants/routesAndApisConstants';
import { getTravelDocUrl } from '../../../utils';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import FaqSection from '../../common/FaqSection';

const BookingDetails = () => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { isRTL } = useDirection();
  const { agencyAccount } = useSiteContext();
  const { authenticatedAction } = useAuth();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const { isWhiteLabelBrand, travelDocUrl, faqSectionPerSlug } = useOrderDetails();
  const faqSectionSlug = p('ContactUs.BookingDetails.Faq.Slug');
  const actualTravelDocUrl = getTravelDocUrl(travelDocUrl, isRTL);

  const handleShowTravelDocument = (event: MouseEvent) => {
    event.preventDefault();

    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Contact',
        action: 'Click',
        label: 'TRAVEL_DOCUMENT',
        property: 'BOOKING_DETAILS',
      });
    }
    authenticatedAction(() => window.open(actualTravelDocUrl, '_blank'));
  };

  const handleOnItineraryClick = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Contact',
        action: 'Click',
        label: 'TRAVEL_ITINERARY',
        property: 'BOOKING_DETAILS',
      });
    }
  };

  const handleOnAirlinesClick = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Contact',
        action: 'Click',
        label: 'AIRLINE_CONTACTS',
        property: 'BOOKING_DETAILS',
      });
    }
  };

  return (
    <TopicsContentWrapper
      chatType="oriana"
      leftColumnContent={
        <Stack spacing={24}>
          <Text asHtml>{t('ContactUs.Subtopic.BookingDetails.Description')}</Text>
          <Stack spacing={8}>
            <a
              aria-label={`${t('ContactUs.Subtopic.BookingDetails.TravelDocument.Link.Text')}, ${t(
                'ScreenReader.Link.OpensInNewTab.AriaLabel.Text',
              )}`}
              data-testid="contact-us-travel-document-button"
              href={actualTravelDocUrl}
              onClick={() => handleShowTravelDocument}
              rel="noreferrer"
              target="_blank"
            >
              <Inline spacing={8}>
                {t('ContactUs.Subtopic.BookingDetails.TravelDocument.Link.Text')}
                <Share aria-hidden="true" mirrored={isRTL} size={20} />
              </Inline>
            </a>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              data-testid="itinerary-link"
              onClick={handleOnItineraryClick}
              rel="noreferrer"
              target="_blank"
              to={`${routes.ORDER_DETAILS}?show-itinerary=true`}
            >
              {t('ContactUs.Subtopic.BookingDetails.Itinerary.Link.Text')}
            </Link>
          </Stack>
          <Text asHtml>{t('ContactUs.Subtopic.BookingDetails.FlightUpdates.Section')}</Text>
          {!isWhiteLabelBrand && !agencyAccount && (
            <Stack spacing={8}>
              <Text asHtml>{t('ContactUs.Subtopic.BookingDetails.App.Section')}</Text>
              <Link
                className={linkStyles}
                data-testid="order-details-app-link"
                target="_blank"
                to={routes.MOBILE_APP_DOWNLOAD}
              >
                {t('OrderDetails.Banner.App.Link.Text')}
              </Link>
            </Stack>
          )}
          <Stack spacing={8}>
            <Text asHtml>{t('ContactUs.Subtopic.BookingDetails.Airlines.Section')}</Text>
            <a
              aria-label={`${t('ContactUs.Subtopic.BookingDetails.Airlines.Link.Text')}, ${t(
                'ScreenReader.Link.OpensInNewTab.AriaLabel.Text',
              )}`}
              data-testid="airlines-link"
              href="/rf/carriers"
              onClick={handleOnAirlinesClick}
              rel="noreferrer"
              target="_blank"
            >
              <Inline spacing={8}>
                {t('ContactUs.Subtopic.BookingDetails.Airlines.Link.Text')}
                <Share aria-hidden="true" mirrored={isRTL} size={20} />
              </Inline>
            </a>
          </Stack>
          {faqSectionPerSlug?.[faqSectionSlug] && (
            <FaqSection
              headingAs="h5"
              headingLevel={4}
              padding={0}
              section={faqSectionPerSlug[faqSectionSlug]}
            />
          )}
        </Stack>
      }
      snowplowProperty="BOOKING_DETAILS"
    />
  );
};

export default BookingDetails;
